import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { StripeConfig } from "@/components/host-payment-methods";
import { getActiveStripePublishableKey, ACTIVE_STRIPE_MODE } from "@/lib/stripe-publishable-key";

export type StripeReadiness = {
  secretKeyConfigured: boolean;
  publishableKeyConfigured: boolean;
  ready: boolean;
};

export function deriveStripeReadiness(
  serverConfig: StripeConfig | undefined,
  bundlePublishableKey: string | undefined,
): StripeReadiness | null {
  if (!serverConfig) return null;
  const publishableKeyConfigured = !!(bundlePublishableKey && bundlePublishableKey.length > 0);
  return {
    secretKeyConfigured: serverConfig.secretKeyConfigured,
    publishableKeyConfigured,
    ready: serverConfig.secretKeyConfigured && publishableKeyConfigured,
  };
}

export function StripeSetupBanner({
  secretKeyConfigured,
  publishableKeyConfigured,
}: {
  secretKeyConfigured: boolean;
  publishableKeyConfigured: boolean;
}) {
  const isTest = ACTIVE_STRIPE_MODE === "test";
  const missing: string[] = [];
  if (!secretKeyConfigured) missing.push(isTest ? "STRIPE_SECRET_TEST_KEY (server)" : "STRIPE_SECRET_KEY (server)");
  if (!publishableKeyConfigured) missing.push(isTest ? "VITE_STRIPE_PUBLISHABLE_TEST_KEY (client)" : "VITE_STRIPE_PUBLISHABLE_KEY (client)");
  return (
    <Card className="p-4 border-amber-300 bg-amber-50" data-testid="banner-stripe-not-configured">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-amber-900 text-sm">Stripe isn't fully configured</p>
          <p className="text-xs text-amber-800 mt-0.5">
            Saving cards and approving vendor payouts will fail until your admin sets the missing key{missing.length > 1 ? "s" : ""}:{" "}
            <span className="font-mono" data-testid="text-stripe-missing-keys">{missing.join(", ")}</span>.
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Add the missing key{missing.length > 1 ? "s" : ""} in the{" "}
            <a
              href="https://docs.replit.com/replit-workspace/workspace-features/secrets"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              data-testid="link-replit-secrets-docs"
            >
              Replit Secrets pane
            </a>
            , then redeploy.
          </p>
          <a
            href="https://docs.stripe.com/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-amber-900 underline mt-1 inline-block"
            data-testid="link-stripe-setup-docs"
          >
            Stripe API key setup docs →
          </a>
        </div>
      </div>
    </Card>
  );
}

// Page-level wrapper: queries the server config probe and renders the
// banner only when either half is missing. The publishable side is read
// from the client bundle, not the server's process env, so a build that
// shipped without the key still surfaces the banner.
export function StripeSetupBannerSection() {
  const stripeConfigQuery = useQuery<StripeConfig>({
    queryKey: ["/api/app/billing/stripe-config"],
  });
  const readiness = deriveStripeReadiness(stripeConfigQuery.data, getActiveStripePublishableKey());
  if (!readiness || readiness.ready) return null;
  return (
    <StripeSetupBanner
      secretKeyConfigured={readiness.secretKeyConfigured}
      publishableKeyConfigured={readiness.publishableKeyConfigured}
    />
  );
}
